import { animated, useSpring } from '@react-spring/web';
import { css, StyleDeclarationValue } from 'aphrodite';
import { Observer, observer } from 'mobx-react';
import * as React from 'react';
import EmailEditor, { Design, SimpleMergeTag } from 'react-email-editor';
import { HtmlNewsletterEditorDefaultEditorOptions } from '.';
import { isIE11 } from '../../../models/Browser';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { useToggle } from '../../../models/hooks/useToggle';
import { useEventLogging } from '../../../models/Logging';
import { Token } from '../../../models/Token';
import { useContactCustomFields } from '../../../queries/Contact';
import { IOperationResultNoValue, ITemplate } from '../../../viewmodels/AppViewModels';
import { HtmlNewsletter } from '../../../viewmodels/HtmlNewletterViewModels';
import { useTheme } from '../../components-v2/Theme/theme';
import { baseStyleSheet } from '../../styles/styles';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Dropdown } from '../Dropdown';
import { HtmlNewsletterPreview } from '../HtmlNewsletterPreview';
import { PlaceholdersPlugin } from '../richContent/RichContentDocumentEditor/plugins';
import { EditItemPenIcon } from '../svgs/icons/EditItemPenIcon';
import { PaperPlaneIcon } from '../svgs/icons/PaperPlaneIcon';
import { TextInput } from '../TextInput';
import { PopoverType, TinyPopover } from '../TinyPopover';
import { styleSheet } from './styles';

export interface IHtmlNewsletterEditorTabsContext {
	newsletter?: HtmlNewsletter;
	showPreview?(show: boolean): void;
}

export interface IHtmlNewsletterEditorBodyContext {
	initialDesign?: Design;
	newsletter?: HtmlNewsletter;
	onEditorLoaded?(editor?: EmailEditor): void;
}

export interface IHtmlNewsletterEditorHeaderContext {
	newsletter?: HtmlNewsletter;
	onEditNameClicked?(e: React.MouseEvent<HTMLElement>): void;
	onSaveAsNewTemplateClicked?(e: React.MouseEvent<HTMLElement>): void;
	onSaveClicked(e: React.MouseEvent<HTMLElement>): void;
	onDownloadPDFClicked(e: React.MouseEvent<HTMLElement>): void;
	onDownloadHTMLClicked(e: React.MouseEvent<HTMLElement>): void;
	onAutoSave(): Promise<ITemplate | null>;
	onSendClicked?(e: React.MouseEvent<HTMLElement>): void;
	onSendTestEmailClicked?(e: React.MouseEvent<HTMLElement>): void;
	autoSaveEnabled?: boolean;
	toggleAutoSave(state: boolean): void;
}

export const HtmlNewsletterEditorHeaderContext = React.createContext<IHtmlNewsletterEditorHeaderContext>({
	toggleAutoSave: () => {
		console.error('[Context Undefined Error]: toggleAutoSave must be defined');
	},
	onAutoSave: () => {
		console.error('[Context Undefined Error]: onAutoSave must be defined');
		return null;
	},
	onSaveClicked: () => {
		console.error('[Context Undefined Error]: onSaveClicked must be defined');
	},
	onDownloadPDFClicked: () => {
		console.error('[Context Undefined Error]: onDownloadPDFClicked must be defined');
	},
	onDownloadHTMLClicked: () => {
		console.error('[Context Undefined Error]: onDownloadHTMLClicked must be defined');
	},
});
export const HtmlNewsletterEditorBodyContext = React.createContext<IHtmlNewsletterEditorBodyContext>({});

export interface IHtmlNewsletterEditorHeaderProps {
	styles?: StyleDeclarationValue[];
	isEditModal?: boolean;
}

export interface IHtmlNewsletterEditorBodyProps {
	style?: React.CSSProperties;
}

export const HtmlNewsletterEditorSaveButton: React.FC<
	{ styles?: StyleDeclarationValue[] } & React.DetailedHTMLProps<
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		HTMLButtonElement
	> &
		React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
> = props => {
	const { styles = [], ...restProps } = props;

	const context = React.useContext(HtmlNewsletterEditorHeaderContext);
	if (!context.newsletter?.canEdit) {
		return null;
	}
	return (
		<button
			{...restProps}
			className={`${props.className || ''} ${css(baseStyleSheet.ctaButton, styles)}`}
			disabled={props.disabled || context?.autoSaveEnabled}
			onClick={props.onClick || context?.onSaveClicked}
		>
			<span>Save</span>
		</button>
	);
};

export const HtmlNewsletterEditorExportAsPDFButton: React.FC<
	React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
> = props => {
	const context = React.useContext(HtmlNewsletterEditorHeaderContext);

	if (!context.newsletter?.canEdit) {
		return null;
	}

	return (
		<Dropdown
			anchor={
				<Button kind='primary' className={`${props.className || ''}`} disabled={props.disabled}>
					<span>Export</span>
				</Button>
			}
			isOpen={false}
			className={css(styleSheet.menu)}
			openOnClick={true}
		>
			<>
				<div className={css(styleSheet.menuItem)} onClick={context.onDownloadPDFClicked}>
					<span style={styleSheet.menuItemLabel}>As PDF</span>
				</div>

				<div className={css(styleSheet.menuItem)} onClick={context.onDownloadHTMLClicked}>
					<span style={styleSheet.menuItemLabel}>As HTML</span>
				</div>
			</>
		</Dropdown>
	);
};

export const HtmlNewsletterEditorSaveAsNewButton: React.FC<
	{ styles?: StyleDeclarationValue[] } & React.DetailedHTMLProps<
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		HTMLButtonElement
	>
> = props => {
	const context = React.useContext(HtmlNewsletterEditorHeaderContext);
	const { styles = [], ...restProps } = props;
	return (
		<button
			{...restProps}
			className={`${props.className || ''} ${css(baseStyleSheet.ctaButton, styles)}`}
			disabled={props.disabled}
			onClick={props.onClick || context?.onSaveAsNewTemplateClicked}
		>
			<span>Save as New Template</span>
		</button>
	);
};

export const HtmlNewsletterEditorAutoSaveCheckbox: React.FC = () => {
	const { toggleAutoSave, autoSaveEnabled } = React.useContext(HtmlNewsletterEditorHeaderContext);
	return (
		<Checkbox
			checked={autoSaveEnabled}
			id='enable-html-autosave'
			onChange={(e: React.ChangeEvent<HTMLInputElement>) => toggleAutoSave(e.target.checked)}
		>
			<div className={css(styleSheet.checkboxText)}>Autosave</div>
		</Checkbox>
	);
};

export const HtmlNewsletterEditorSubjectInput = ({
	value,
	onChange,
}: {
	value: string;
	onChange: (value: string) => void;
}) => {
	const { newsletter } = React.useContext(HtmlNewsletterEditorHeaderContext);

	const label = (
		<span>
			Subject:
			<span className={css(baseStyleSheet.required)}>*</span>
			&nbsp;
		</span>
	);
	return (
		<div
			className={css(
				styleSheet.htmlNewsletterEditorSubjectHeader,
				newsletter?.canEdit ? styleSheet.htmlNewsletterEditorSubjectHeaderEditable : null
			)}
		>
			<TextInput
				disabled={!newsletter.canEdit}
				inputId='html-newslettter-editor-subject'
				leftAccessory={label}
				onChange={ev => onChange(ev.target.value)}
				type='text'
				value={value || ''}
			/>
		</div>
	);
};

export const HtmlNewsletterEditorSendButton: React.FC<
	{ styles?: StyleDeclarationValue[] } & React.DetailedHTMLProps<
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		HTMLButtonElement
	>
> = props => {
	const { styles = [], ...restProps } = props;

	const context = React.useContext(HtmlNewsletterEditorHeaderContext);
	const info = 'Save your template in order to send.';
	const disabled = props.disabled || (!!context.newsletter?.name && context.newsletter.name === 'Untitled Newsletter');
	const [isOpen, , setOpen] = useToggle(false);
	const sendNewsletterButton = (
		<button
			{...restProps}
			className={`${props.className || ''} ${css(styleSheet.sendButton, styles)}`}
			disabled={disabled}
			onClick={props.onClick || context?.onSendClicked}
		>
			<span>Send out HTML Newsletter</span>
		</button>
	);
	return (
		<div>
			{disabled ? (
				<div className={`${css(baseStyleSheet.truncateText)}`} onMouseEnter={setOpen(true)} onMouseOut={setOpen(false)}>
					<TinyPopover
						anchor={sendNewsletterButton}
						isOpen={isOpen}
						dismissOnOutsideAction={true}
						onRequestClose={setOpen(false)}
						align='start'
						placement={['top']}
						type={PopoverType.lightBlue}
						contentStyles={[styleSheet.infoMessageContainer]}
					>
						<div className={css(styleSheet.infoMessage)}>{info}</div>
					</TinyPopover>
				</div>
			) : (
				sendNewsletterButton
			)}
		</div>
	);
};

export const HtmlNewsletterEditorHeader: React.FC<
	React.PropsWithChildren<IHtmlNewsletterEditorHeaderProps>
> = props => {
	const { colors } = useTheme();
	const context = React.useContext(HtmlNewsletterEditorHeaderContext);
	if (!context) {
		return null;
	}
	return (
		<div className={css(styleSheet.header, ...(props.styles || []))}>
			<div className={css(styleSheet.headerRow)}>
				<div className={css(styleSheet.templateNameAndCTAButtons)}>
					<div className={css(styleSheet.nameContainer)}>
						<div className={css(baseStyleSheet.horizontalStack)}>
							<div className={css(styleSheet.name, baseStyleSheet.truncateText)}>
								<Observer>{() => <>{context.newsletter?.name || 'Untitled Newsletter'}</>}</Observer>
							</div>
							{context.newsletter?.canEdit && (
								<button className={css(styleSheet.editNameButton)} onClick={context.onEditNameClicked}>
									<EditItemPenIcon />
								</button>
							)}
						</div>
					</div>
					<div className={css(baseStyleSheet.horizontalStack, styleSheet.headerActions)}>{props.children}</div>
				</div>
				{!props?.isEditModal ? (
					<button
						className={css(baseStyleSheet.horizontalStack, styleSheet.headerSendTestEmailCta)}
						onClick={context.onSendTestEmailClicked}
					>
						<PaperPlaneIcon color={colors.primary.main} />
						<span>Send a Test</span>
					</button>
				) : null}
			</div>
		</div>
	);
};

const HtmlNewsletterEditorReadOnlyBodyContent: React.FC<{
	newsletter?: HtmlNewsletter;
	style?: React.CSSProperties;
}> = observer(({ newsletter, style }) => {
	const previewStylesRef = React.useRef<StyleDeclarationValue[]>([styleSheet.editorPreviewer]);
	const [warningProps, setWarningProps] = React.useState({
		mount: true,
		show: true,
	});

	const spring = useSpring({
		from: { opacity: warningProps.show ? 0 : 1 },
		to: { opacity: warningProps.show ? 1 : 0 },
		onRest: () => {
			setWarningProps({ ...warningProps, mount: warningProps.show });
		},
	});

	const onCtaClicked = React.useCallback(() => {
		setWarningProps({ ...warningProps, show: false });
	}, [warningProps]);
	return (
		<div className={css(styleSheet.editorPreviewerContainer)} style={style}>
			<HtmlNewsletterPreview styles={previewStylesRef.current} template={newsletter?.template} />
			{!!warningProps.mount && isIE11() && (
				<animated.div style={spring} className={css(styleSheet.editorPreviewerWarning)}>
					<div className={css(styleSheet.editorPreviewerWarningCard, baseStyleSheet.verticalStack)}>
						<div>
							<div className={css(styleSheet.editorPreviewerWarningCardTitle)}>
								Editing is not supported in this browser.
							</div>
							Please use Chrome, FireFox, MS Edge or Safari to enable editing.
						</div>
						<button className={css(baseStyleSheet.ctaButtonSmall)} onClick={onCtaClicked}>
							<span>Ok</span>
						</button>
					</div>
				</animated.div>
			)}
		</div>
	);
});

export const HtmlNewsletterEditorBody: React.FC<IHtmlNewsletterEditorBodyProps> = props => {
	const logger = useEventLogging('HtmlNewsletterEditorBody');
	const userSession = useUserSession();
	const { onEditorLoaded, initialDesign: initDesign, newsletter } = React.useContext(HtmlNewsletterEditorBodyContext);
	const { autoSaveEnabled, onAutoSave } = React.useContext(HtmlNewsletterEditorHeaderContext);
	const customFieldsQuery = useContactCustomFields({
		refetchOnWindowFocus: false,
		enabled: userSession.account?.additionalInfo?.hasCustomContactFields,
	});
	const unlayerOptions = React.useMemo(() => {
		const options = { ...HtmlNewsletterEditorDefaultEditorOptions };
		if (customFieldsQuery.data?.fields?.length) {
			const placeholders = PlaceholdersPlugin.getCustomFieldPlaceholders(customFieldsQuery.data.fields);
			for (const placeholder of placeholders) {
				options.mergeTags.contact.mergeTags[placeholder.value] = {
					name: placeholder.text,
					value: Token.getPlaceholderHtmlStringValue(placeholder),
				} as SimpleMergeTag;
			}
		}
		return options;
	}, [customFieldsQuery.data]);

	const editorRef = React.useRef<EmailEditor>(null);
	const initialDesign = React.useRef<Design>(null);
	const [isLoaded, setIsLoaded] = React.useState(false);
	const onLoaded = () => {
		setIsLoaded(true);
	};

	React.useEffect(() => {
		if (editorRef.current) {
			onEditorLoaded(editorRef.current);
			if (initDesign) {
				initialDesign.current = initDesign;
			}
		}
		if (isLoaded && editorRef.current) {
			const unlayer = editorRef.current.editor;
			unlayer.registerCallback<{ attachments: File[] }, { url?: string; progress?: number }>('image', (file, done) => {
				// Handle file upload here
				if (newsletter && file?.attachments?.[0]) {
					done({ progress: 1 });
					newsletter.files
						.uploadImages([file.attachments[0]], false, true)
						?.then(x => {
							done({ progress: 100, url: x.succeeded[0]?.url });
						})
						?.catch((err: IOperationResultNoValue) => {
							logger.logApiError('ImageUpload-Error', err);
							done({ progress: 0, url: null });
						});
				} else {
					done({ url: null });
				}
			});

			if (initialDesign.current) {
				unlayer.loadDesign(initialDesign.current);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded]);

	React.useEffect(() => {
		if (isLoaded && editorRef.current) {
			const callback = () => {
				if (autoSaveEnabled) {
					onAutoSave();
				}
			};
			editorRef.current.editor.registerCallback('design:updated', callback);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded, autoSaveEnabled]);

	if (!newsletter.canEdit) {
		return <HtmlNewsletterEditorReadOnlyBodyContent newsletter={newsletter} style={props.style} />;
	}
	return (
		<>
			{/* @ts-ignore */}
			<EmailEditor onReady={onLoaded} options={unlayerOptions} ref={editorRef} style={props.style} />
		</>
	);
};
